"use client";

import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ArrowRight, ShieldCheck, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const nearbyAreas = [{
  name: "Farnborough",
  to: "/AreasWeCoverPage"
}, {
  name: "Fleet",
  to: "/AreasWeCoverPage"
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function QuoteCta() {
  return <section id="finalquotecta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="bg-secondary text-secondary-foreground border-border/20 shadow-xl overflow-hidden py-0">
            <CardContent className="p-8 sm:p-10 md:p-14">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                {/* Left side: Main text and action */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary-foreground border border-primary/30 text-xs font-semibold uppercase tracking-wide">
                    <ShieldCheck className="size-3.5 text-primary" />
                    <span>Safety-Led Scaffolding Contractors</span>
                  </div>

                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                    Request a scaffolding quote
                  </h2>

                  <p className="text-secondary-foreground/80 text-base sm:text-lg leading-relaxed max-w-xl">
                    Get in touch with our contracts team to discuss your project
                    access requirements or send over architectural drawings for a
                    clear, prompt estimate.
                  </p>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                    <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base h-12 px-6 shadow-md transition-all">
                      <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2 justify-center">
                        <span>Request a quote</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-white hover:bg-secondary-foreground/10 hover:text-white font-medium text-base h-12 px-6 bg-transparent">
                      <a href="tel:01344929900" className="inline-flex items-center gap-2 justify-center text-white hover:text-white focus-visible:text-white">
                        <Phone className="size-4 text-primary" />
                        <span>01344 929900</span>
                      </a>
                    </Button>
                  </div>

                  {/* Accreditations row */}
                  <div className="pt-2 flex flex-wrap items-center gap-y-2 gap-x-5 text-xs text-secondary-foreground/70 font-medium">
                    {accreditations.map((item, i) => <div data-index={i} key={item} className="flex items-center gap-1.5">
                        <span className="size-1.5 rounded-full bg-primary" />
                        <span>{item}</span>
                      </div>)}
                  </div>
                </div>

                {/* Right side: Contact quick-access card */}
                <div className="lg:col-span-5">
                  <div className="bg-card text-card-foreground rounded-xl p-6 sm:p-8 border border-border shadow-md space-y-5">
                    <h3 className="text-lg font-bold text-foreground">
                      Direct contact
                    </h3>
                    <p className="text-sm text-muted-foreground leading-normal">
                      For tender enquiries, emergency site visits, or commercial contracts.
                    </p>

                    <div className="space-y-4 pt-1">
                      <a href="tel:01344929900" className="flex items-center gap-3.5 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all text-foreground group">
                        <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Phone className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground font-medium">
                            Phone enquiries
                          </div>
                          <div className="text-sm font-semibold tracking-tight text-foreground truncate">
                            01344 929900
                          </div>
                        </div>
                      </a>

                      <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-3.5 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-all text-foreground group">
                        <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Mail className="size-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground font-medium">
                            Email tender drawings
                          </div>
                          <div className="text-sm font-semibold tracking-tight text-foreground truncate">
                            quotes@bmfscaffolding.co.uk
                          </div>
                        </div>
                      </a>
                    </div>

                    <div className="pt-2 text-xs text-muted-foreground">
                      <span>Covering Berkshire, Surrey, Hampshire, Buckinghamshire, London &amp; Home Counties.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nearby areas footer inside card */}
              <div className="mt-10 pt-6 border-t border-border/20">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs">
                  <div className="flex items-center gap-2 text-secondary-foreground/70">
                    <MapPin className="size-3.5 text-primary shrink-0" />
                    <span>Nearby areas covered:</span>
                    <div className="flex items-center gap-3 text-secondary-foreground/90 font-medium">
                      {nearbyAreas.map((area, i) => <div data-index={i} key={area.name} className="flex items-center gap-3">
                          <Link to={area.to} className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                            {area.name}
                          </Link>
                          {i < nearbyAreas.length - 1 && <span className="text-secondary-foreground/40">•</span>}
                        </div>)}
                    </div>
                  </div>

                  <Link to="/AreasWeCoverPage" className="text-secondary-foreground/70 hover:text-white transition-colors underline-offset-4 hover:underline">
                    View all service areas
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}